<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Form, Field } from 'vee-validate'
import WidgetModalShell from '@/components/Dashboard/Modals/WidgetModalShell.vue'
import ButtonLoader from '@/components/Globals/Feedbacks/ButtonLoader.vue'
import DropdownSensors from '@/components/Globals/Inputs/DropdownSensors.vue'
import { useI18n } from '@/composable/useI18n'
import {
  filterCorrelationSensors,
  isCorrelationSensorCompatible,
} from '@/utils/dashboard/time-series-correlation'
import type { ISensor } from '@/interfaces/entities/sensor'
import type { IWidget, IWidgetFormData } from '@/interfaces/components/dashboard/widget'
import { timeSeriesWidgetSchema } from '@/schemas/WidgetModalSchema'

const props = withDefaults(
  defineProps<{
    sensors: ISensor[]
    initialData?: IWidget
    loading?: boolean
  }>(),
  { initialData: undefined, loading: false },
)

const emit = defineEmits<{
  save: [formData: IWidgetFormData]
  cancel: []
}>()

const { t } = useI18n()
const selectedSensor = ref<ISensor | null>(null)
const selectedSecondarySensor = ref<ISensor | null>(null)

const secondaryDropdownKey = ref(0)

const fieldLabelClass = 'mb-1.5 block text-xs font-bold uppercase tracking-wider text-body'
const fieldInputClass =
  'w-full border border-stroke bg-gray-2 px-3 py-2.5 text-sm text-black outline-none focus:border-primary'
const fieldErrorClass = 'mt-1 text-sm text-red'

const modalTitle = computed(() =>
  props.initialData
    ? t('custom_dashboards.widget_modal.time_series.title_edit')
    : t('custom_dashboards.widget_modal.time_series.title_create'),
)

const correlationSensorOptions = computed(() =>
  filterCorrelationSensors(props.sensors, selectedSensor.value),
)

const initialValues = computed(() => {
  if (props.initialData) {
    const sensorId = props.initialData.config.sensor_ids[0] || ''
    const secondarySensorId = props.initialData.config.sensor_ids[1] || ''
    return {
      title: props.initialData.title,
      sensorId,
      secondarySensorId,
    }
  }

  return {
    title: t('custom_dashboards.widget_modal.time_series.default_title'),
    sensorId: '',
    secondarySensorId: '',
  }
})

watch(
  () => [props.initialData, props.sensors] as const,
  () => {
    if (!props.initialData) {
      selectedSensor.value = null
      selectedSecondarySensor.value = null
      return
    }

    const sensorId = props.initialData.config.sensor_ids[0] || ''
    const secondarySensorId = props.initialData.config.sensor_ids[1] || ''
    selectedSensor.value = props.sensors.find((s) => String(s.id) === sensorId) || null
    selectedSecondarySensor.value =
      props.sensors.find((s) => String(s.id) === secondarySensorId) || null
  },
  { immediate: true },
)

function clearSecondarySensor(setFieldValue: (field: string, value: unknown) => void) {
  selectedSecondarySensor.value = null
  setFieldValue('secondarySensorId', '')
  secondaryDropdownKey.value += 1
}

function onSensorSelected(sensor: ISensor, setFieldValue: (field: string, value: unknown) => void) {
  selectedSensor.value = sensor
  setFieldValue('sensorId', String(sensor.id))

  if (
    selectedSecondarySensor.value &&
    !isCorrelationSensorCompatible(sensor, selectedSecondarySensor.value)
  ) {
    clearSecondarySensor(setFieldValue)
  }
}

function onSecondarySensorSelected(
  sensor: ISensor,
  setFieldValue: (field: string, value: unknown) => void,
) {
  selectedSecondarySensor.value = sensor
  setFieldValue('secondarySensorId', String(sensor.id))
}

function onSubmit(values: { title: string; sensorId: string; secondarySensorId?: string }) {
  emit('save', {
    title: values.title,
    sensorId: values.sensorId,
    secondarySensorId: values.secondarySensorId || undefined,
  })
}
</script>

<template>
  <WidgetModalShell :loading="loading" @cancel="emit('cancel')">
    <template #title>
      <h2 class="font-satoshi mb-6 text-xl font-bold text-black">{{ modalTitle }}</h2>
    </template>

    <Form
      :validation-schema="timeSeriesWidgetSchema"
      :initial-values="initialValues"
      @submit="onSubmit"
      v-slot="{ setFieldValue, errors }"
    >
      <div class="space-y-5">
        <Field name="title" v-slot="{ field, errorMessage }">
          <div>
            <label :class="fieldLabelClass">
              {{ t('custom_dashboards.widget_modal.time_series.fields.title') }}
            </label>
            <input
              v-bind="field"
              type="text"
              :class="fieldInputClass"
              :placeholder="t('custom_dashboards.widget_modal.time_series.placeholders.title')"
            />
            <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
          </div>
        </Field>

        <div>
          <label :class="fieldLabelClass">
            {{ t('custom_dashboards.widget_modal.time_series.fields.sensor') }}
          </label>
          <DropdownSensors
            name="sensorId"
            :options="sensors"
            :already-selected-option="selectedSensor || undefined"
            :constrain-to-parent="true"
            @option-selected="(sensor: ISensor) => onSensorSelected(sensor, setFieldValue)"
          />
          <p v-if="errors.sensorId" :class="fieldErrorClass">{{ errors.sensorId }}</p>
        </div>

        <div>
          <label :class="fieldLabelClass">
            {{ t('custom_dashboards.widget_modal.time_series.fields.secondary_sensor') }}
          </label>
          <DropdownSensors
            :key="secondaryDropdownKey"
            name="secondarySensorId"
            :options="correlationSensorOptions"
            :already-selected-option="selectedSecondarySensor || undefined"
            :constrain-to-parent="true"
            @option-selected="(sensor: ISensor) => onSecondarySensorSelected(sensor, setFieldValue)"
          />
          <p v-if="errors.secondarySensorId" :class="fieldErrorClass">
            {{ errors.secondarySensorId }}
          </p>
        </div>

        <div class="border-l-4 border-blue bg-blue-50 px-4 py-3">
          <p class="text-sm leading-relaxed text-body">
            {{ t('custom_dashboards.widget_modal.time_series.info') }}
          </p>
        </div>
      </div>

      <div class="mt-6 border-t border-stroke pt-6">
        <div class="flex justify-end gap-4">
          <button
            type="button"
            class="text-xs font-medium uppercase tracking-wide text-body hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="loading"
            @click="emit('cancel')"
          >
            {{ t('custom_dashboards.widget_modal.time_series.actions.cancel') }}
          </button>
          <button
            type="submit"
            class="flex h-10 min-w-[7rem] items-center justify-center bg-blue px-6 py-2.5 text-xs font-medium uppercase tracking-wide text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="loading"
          >
            <ButtonLoader v-if="loading" />
            <span v-else>
              {{
                initialData
                  ? t('custom_dashboards.widget_modal.time_series.actions.save')
                  : t('custom_dashboards.widget_modal.time_series.actions.create')
              }}
            </span>
          </button>
        </div>
      </div>
    </Form>
  </WidgetModalShell>
</template>

<style scoped>
.font-satoshi {
  font-family: 'Satoshi', sans-serif;
}
</style>
